<template>
  <header class="header" :class="{ 'header--scrolled': isScrolled }">
    <div class="header__container">
      <router-link to="/" class="header__logo" @click="closeMobileMenu">
        <span class="logo-bracket">&lt;</span>
        <animatable-text class="logo-text" :text="'William S. Ness'" :delay="0" :custom-css="makeHeaderTextCss"/>
        <span class="logo-bracket">/&gt;</span>
      </router-link>

      <button 
        class="header__mobile-toggle" 
        @click="toggleMobileMenu"
        aria-label="Toggle navigation menu"
      >
        <span class="hamburger" :class="{ 'hamburger--active': isMobileMenuOpen }">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      <nav class="header__nav" :class="{ 'header__nav--open': isMobileMenuOpen }">
        <router-link 
          :to="{ path: '/', hash: '#projects' }" 
          :class="{ 'nav-link': true, 'nav-link-full-match-active': route.fullPath === '/#projects' }"
          @click="closeMobileMenu"
        >
          Projects
        </router-link>
        <router-link 
          :to="{ path: '/', hash: '#contact' }" 
          :class="{ 'nav-link': true, 'nav-link-full-match-active': route.fullPath === '/#contact' }"
          @click="closeMobileMenu"
        >
          Contact
        </router-link>
        <a 
          href="/cv/CV.pdf" 
          target="_blank" 
          rel="noopener noreferrer"
          class="nav-link nav-link--external"
          @click="closeMobileMenu"
        >
          CV
        </a>
        <a 
          href="https://github.com/jamme05" 
          target="_blank" 
          rel="noopener noreferrer"
          class="nav-link nav-link--external"
          @click="closeMobileMenu"
        >
          GitHub
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </a>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import AnimatableText from '../AnimatableText.vue';

const route = useRoute();

const isScrolled = ref(false)
const isMobileMenuOpen = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  // Prevent body scroll when menu is open
  document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.style.overflow = ''
}

const makeHeaderTextCss = ( index, length ) => {
  return `rotate: ${90 * index / length}deg;`
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.body.style.overflow = ''
})

</script>

<style>

.logo-text span {
  display: inline-block;
  transition: all 0.3s;
}

.header__logo:hover .logo-text span {
  rotate: 0deg !important;
}

</style>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-header);
  background: rgba(10, 14, 10, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid transparent;
  transition: all var(--transition-normal);
}

.header--scrolled {
  background: rgba(10, 14, 10, 0.95);
  border-bottom-color: var(--border-color);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.header__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header__logo {
  font-family: var(--font-mono);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--neon-cyan);
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: all var(--transition-fast);
}

.header__logo:hover {
  text-shadow: var(--glow-cyan);
  transform: scale(1.05);
}

.logo-bracket {
  color: var(--neon-magenta);
}

.logo-text {
  margin: 0 0.25rem;
  white-space: nowrap;
  max-width: 1ch;
  overflow: hidden;
  transition: max-width 0.4s;
}

.header__logo:hover .logo-text {
  max-width: 100%;
}

.header__mobile-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  z-index: calc(var(--z-header) + 10);
}

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 24px;
}

.hamburger span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--neon-cyan);
  transition: all var(--transition-normal);
  box-shadow: var(--glow-cyan);
}

.hamburger--active span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 2px);
}

.hamburger--active span:nth-child(2) {
  opacity: 0;
}

.hamburger--active span:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -5px);
}

.header__nav {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-link {
  font-family: var(--font-mono);
  font-size: 0.95rem;
  color: var(--text-secondary);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all var(--transition-fast);
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--neon-cyan);
  box-shadow: var(--glow-cyan);
  transition: width var(--transition-normal);
}

.nav-link:hover {
  color: var(--neon-cyan);
}

.nav-link:hover::after {
  width: 100%;
}

.nav-link.nav-link-full-match-active {
  color: var(--neon-cyan);
}

.nav-link.nav-link-full-match-active::after {
  width: 100%;
}

.nav-link--external svg {
  width: 14px;
  height: 14px;
  opacity: 0.7;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .header__container {
    padding: 1rem;
  }

  .header__mobile-toggle {
    display: block;
  }

  .header__nav {
    position: fixed;
    top: 100%;
    right: 0;
    width: 70%;
    max-width: 300px;
    background: var(--bg-darker);
    border-left: 1px solid var(--border-color);
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    padding: 2rem;
    transform: translateX(100%);
    transition: transform var(--transition-normal);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
  }

  .header__nav--open {
    transform: translateX(0);
  }

  .header__logo:hover .logo-text {
    max-width: 1ch;
  }

  .nav-link {
    font-size: 1.2rem;
    width: 100%;
    padding: 0.5rem 0;
  }

  .nav-link::after {
    bottom: 0;
  }
}

@media (max-width: 480px) {
    .header__nav {
    position: fixed;
    top: 100%;
    right: 0;
    width: 100vw;
    max-width: 100vw;
    background: var(--bg-darker);
    border-left: 1px solid var(--border-color);
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    padding: 2rem;
    transform: translateX(0) translateY(-150%);
    transition: transform var(--transition-normal);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
  }

  .header__nav--open {
    transform: translateY(0);
  }
}
</style>
